import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DownloadIcon from "@mui/icons-material/Download";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";

import api from "../../services/api";

/**
 * Emissão de certificados em lote.
 *
 * A tela existe para a prestação de contas: o gestor recorta o universo de
 * concluintes, decide como o ZIP fica organizado por dentro e recebe o arquivo.
 * A geração roda em fila no servidor, então a página acompanha o progresso em
 * vez de travar esperando.
 */

const rotulos = {
  na_fila: { texto: "Na fila", cor: "default" },
  processando: { texto: "Gerando", cor: "info" },
  concluido: { texto: "Pronto", cor: "success" },
  erro: { texto: "Falhou", cor: "error" },
  expirado: { texto: "Arquivo removido", cor: "warning" },
};

function bytesLegivel(n) {
  const v = Number(n);
  if (!v) return "—";
  if (v < 1024 * 1024) return `${Math.round(v / 1024)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / 1024 / 1024).toFixed(1)} MB`;
  return `${(v / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function dataHora(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch (e) {
    return "—";
  }
}

export default function AdminCertificadosLote() {
  const [opcoes, setOpcoes] = useState(null);
  const [carregandoOpcoes, setCarregandoOpcoes] = useState(true);
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");

  // recorte
  const [programas, setProgramas] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [laboratorios, setLaboratorios] = useState([]);
  const [trilhas, setTrilhas] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");

  // saída
  const [titulo, setTitulo] = useState("");
  const [formato, setFormato] = useState("consolidado");
  const [agrupamento, setAgrupamento] = useState(["ciclo", "municipio", "turma"]);

  // prévia e lotes
  const [previa, setPrevia] = useState(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [lotes, setLotes] = useState([]);

  const filtros = useMemo(
    () => ({
      programas,
      ciclos,
      municipios,
      laboratorios,
      trilhas,
      tipos,
      turmas,
      inicio: inicio || null,
      fim: fim || null,
    }),
    [programas, ciclos, municipios, laboratorios, trilhas, tipos, turmas, inicio, fim]
  );

  useEffect(() => {
    api
      .get("/certificados/lote/opcoes")
      .then((r) => setOpcoes(r?.data ?? null))
      .catch((e) =>
        setErro(e?.response?.data?.error || "Não foi possível carregar os filtros.")
      )
      .finally(() => setCarregandoOpcoes(false));
  }, []);

  const carregarLotes = useCallback(() => {
    api
      .get("/certificados/lote")
      .then((r) => setLotes(Array.isArray(r?.data) ? r.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    carregarLotes();
  }, [carregarLotes]);

  // Enquanto houver lote em andamento, atualiza a lista sozinho — senão o gestor
  // fica clicando em "atualizar" para saber se o arquivo já saiu.
  useEffect(() => {
    const emAndamento = lotes.some((l) =>
      ["na_fila", "processando"].includes(l?.status)
    );
    if (!emAndamento) return undefined;
    const t = setInterval(carregarLotes, 4000);
    return () => clearInterval(t);
  }, [lotes, carregarLotes]);

  const conferir = () => {
    setCarregandoPrevia(true);
    setPrevia(null);
    setErro("");
    api
      .post("/certificados/lote/previa", { filtros })
      .then((r) => setPrevia(r?.data ?? null))
      .catch((e) =>
        setErro(e?.response?.data?.error || "Não foi possível conferir o recorte.")
      )
      .finally(() => setCarregandoPrevia(false));
  };

  const emitir = () => {
    setEnviando(true);
    setErro("");
    api
      .post("/certificados/lote", {
        titulo,
        filtros: { ...filtros, formato },
        agrupamento,
        formato,
      })
      .then(() => {
        setAviso("Lote na fila. O arquivo aparece na lista quando terminar.");
        setPrevia(null);
        carregarLotes();
      })
      .catch((e) =>
        setErro(e?.response?.data?.error || "Não foi possível criar o lote.")
      )
      .finally(() => setEnviando(false));
  };

  const baixar = (lote) => {
    api
      .get(`/certificados/lote/${lote.id}/download`, { responseType: "blob" })
      .then((r) => {
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = lote?.arquivo_nome || `certificados-${lote.id}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(async (e) => {
        // o corpo do erro vem como blob por causa do responseType
        let msg = "Não foi possível baixar o arquivo.";
        try {
          const txt = await e?.response?.data?.text?.();
          msg = JSON.parse(txt)?.error || msg;
        } catch (x) {
          // mantém a mensagem padrão
        }
        setErro(msg);
        carregarLotes();
      });
  };

  const moverNivel = (idx, direcao) => {
    const novo = [...agrupamento];
    const destino = idx + direcao;
    if (destino < 0 || destino >= novo.length) return;
    [novo[idx], novo[destino]] = [novo[destino], novo[idx]];
    setAgrupamento(novo);
  };

  const alternarNivel = (id) => {
    setAgrupamento((atual) =>
      atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]
    );
  };

  const podeEmitir =
    Boolean(titulo.trim()) &&
    previa?.total > 0 &&
    !previa?.acimaDoTeto &&
    !enviando;

  if (carregandoOpcoes) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const niveisDisponiveis = opcoes?.agrupamentosDisponiveis ?? [];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: "auto" }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <FolderZipIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Certificados em lote
        </Typography>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Monte o recorte, escolha como o ZIP fica organizado por dentro e emita. O
        arquivo é gerado no servidor e fica disponível nesta página.
      </Typography>

      {erro ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro("")}>
          {erro}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          1. Quem entra no lote
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              size="small"
              options={opcoes?.programas ?? []}
              getOptionLabel={(o) => o?.nome ?? ""}
              isOptionEqualToValue={(o, v) => o?.id === v?.id}
              onChange={(_, v) => setProgramas(v.map((x) => x.id))}
              renderInput={(p) => (
                <TextField {...p} label="Programa" placeholder="Todos" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              size="small"
              options={opcoes?.ciclos ?? []}
              getOptionLabel={(o) => `${o?.nome ?? ""} (${o?.total ?? 0})`}
              isOptionEqualToValue={(o, v) => o?.id === v?.id}
              onChange={(_, v) => setCiclos(v.map((x) => x.id))}
              renderInput={(p) => (
                <TextField {...p} label="Ciclo" placeholder="Todos" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              size="small"
              options={opcoes?.municipios ?? []}
              getOptionLabel={(o) => `${o?.nome ?? ""} (${o?.total ?? 0})`}
              isOptionEqualToValue={(o, v) => o?.nome === v?.nome}
              onChange={(_, v) => setMunicipios(v.map((x) => x.nome))}
              renderInput={(p) => (
                <TextField {...p} label="Município" placeholder="Todos" />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              size="small"
              options={opcoes?.laboratorios ?? []}
              getOptionLabel={(o) =>
                `${o?.nome ?? ""}${o?.municipio ? ` — ${o.municipio}` : ""}`
              }
              isOptionEqualToValue={(o, v) => o?.id === v?.id}
              onChange={(_, v) => setLaboratorios(v.map((x) => x.id))}
              renderInput={(p) => (
                <TextField {...p} label="Laboratório" placeholder="Todos" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              size="small"
              options={opcoes?.trilhas ?? []}
              getOptionLabel={(o) => `${o?.nome ?? ""} (${o?.total ?? 0})`}
              isOptionEqualToValue={(o, v) => o?.nome === v?.nome}
              onChange={(_, v) => setTrilhas(v.map((x) => x.nome))}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Trilha / Nível"
                  placeholder="Todas"
                  helperText="No Lab são as trilhas; no Academy, os níveis"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              size="small"
              options={opcoes?.tipos ?? []}
              getOptionLabel={(o) => `${o?.nome ?? ""} (${o?.total ?? 0})`}
              isOptionEqualToValue={(o, v) => o?.nome === v?.nome}
              onChange={(_, v) => setTipos(v.map((x) => x.nome))}
              renderInput={(p) => (
                <TextField {...p} label="Tipo de curso" placeholder="Todos" />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              size="small"
              options={opcoes?.turmas ?? []}
              getOptionLabel={(o) =>
                `${o?.titulo ?? ""}${o?.municipio ? ` — ${o.municipio}` : ""} (${o?.total ?? 0})`
              }
              isOptionEqualToValue={(o, v) => o?.id === v?.id}
              onChange={(_, v) => setTurmas(v.map((x) => x.id))}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Turmas específicas"
                  placeholder="Todas as que atendem aos filtros acima"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label="Concluído a partir de"
              InputLabelProps={{ shrink: true }}
              value={inicio}
              onChange={(e) => setInicio(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label="Concluído até"
              InputLabelProps={{ shrink: true }}
              value={fim}
              onChange={(e) => setFim(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={conferir}
              disabled={carregandoPrevia}
            >
              {carregandoPrevia ? "Conferindo…" : "Conferir quantos são"}
            </Button>
          </Grid>
        </Grid>

        {previa ? (
          <Alert
            severity={
              previa.acimaDoTeto ? "warning" : previa.total ? "success" : "info"
            }
            sx={{ mt: 2 }}
          >
            <strong>{previa.total}</strong> certificado(s) neste recorte.
            {previa.acimaDoTeto
              ? ` Acima do teto de ${previa.teto} por lote — divida por ciclo ou município.`
              : ""}
            {previa.total && !previa.acimaDoTeto ? (
              <Box component="span" sx={{ display: "block", mt: 0.5, fontSize: 13 }}>
                Ex.: {previa.amostra?.slice(0, 3).map((a) => a?.aluno_nome).join(" · ")}
              </Box>
            ) : null}
          </Alert>
        ) : null}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          2. Como o ZIP fica organizado
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Marque os níveis de pasta e use as setas para definir a ordem.
            </Typography>
            <Stack spacing={0.5}>
              {niveisDisponiveis.map((n) => {
                const idx = agrupamento.indexOf(n.id);
                const ativo = idx >= 0;
                return (
                  <Stack
                    key={n.id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      px: 1,
                      borderRadius: 1,
                      bgcolor: ativo ? "action.hover" : "transparent",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={ativo}
                          onChange={() => alternarNivel(n.id)}
                        />
                      }
                      label={
                        <span>
                          {ativo ? (
                            <Chip
                              size="small"
                              label={idx + 1}
                              sx={{ mr: 1, height: 20 }}
                            />
                          ) : null}
                          {n.nome}
                        </span>
                      }
                    />
                    {ativo ? (
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => moverNivel(idx, -1)}
                          disabled={idx === 0}
                        >
                          <ArrowUpwardIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moverNivel(idx, 1)}
                          disabled={idx === agrupamento.length - 1}
                        >
                          <ArrowDownwardIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    ) : null}
                  </Stack>
                );
              })}
            </Stack>
          </Grid>

          <Grid item xs={12} md={7}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Prévia da estrutura
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor: "grey.50",
                fontFamily: "monospace",
                fontSize: 12.5,
                whiteSpace: "pre",
                overflowX: "auto",
              }}
            >
              {[
                `${titulo.trim() ? titulo.trim() : "certificados"}.zip`,
                "├── _indice.csv",
                ...agrupamento.map(
                  (id, i) =>
                    `${"│   ".repeat(i)}${i === agrupamento.length - 1 && formato === "individuais" ? "└──" : "├──"} <${
                      niveisDisponiveis.find((n) => n.id === id)?.nome ?? id
                    }>/`
                ),
                formato !== "individuais"
                  ? `${"│   ".repeat(agrupamento.length)}├── _consolidado - <pasta>.pdf`
                  : null,
                formato !== "consolidado"
                  ? `${"│   ".repeat(agrupamento.length)}└── 001 - <Nome do aluno>.pdf`
                  : null,
              ]
                .filter(Boolean)
                .join("\n")}
            </Paper>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Formato dos PDFs
            </Typography>
            <RadioGroup
              value={formato}
              onChange={(e) => setFormato(e.target.value)}
            >
              {(opcoes?.formatos ?? []).map((f) => (
                <FormControlLabel
                  key={f.id}
                  value={f.id}
                  control={<Radio size="small" />}
                  label={
                    <span>
                      {f.nome}{" "}
                      <Typography component="span" variant="caption" color="text.secondary">
                        — {f.ajuda}
                      </Typography>
                    </span>
                  }
                />
              ))}
            </RadioGroup>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          3. Emitir
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
          <TextField
            size="small"
            fullWidth
            label="Nome do lote"
            placeholder="Ex.: Certificados 2º Ciclo 2026 — Arapiraca"
            helperText="Vira o nome do arquivo .zip"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
          />
          <Tooltip
            title={
              !previa
                ? "Confira o recorte antes de emitir"
                : !titulo.trim()
                ? "Dê um nome ao lote"
                : ""
            }
          >
            <span>
              <Button
                variant="contained"
                size="large"
                startIcon={<FolderZipIcon />}
                onClick={emitir}
                disabled={!podeEmitir}
                sx={{ whiteSpace: "nowrap" }}
              >
                {enviando ? "Enviando…" : "Gerar ZIP"}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Lotes emitidos
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={carregarLotes}>
          Atualizar
        </Button>
      </Stack>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Lote</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell align="right">Certificados</TableCell>
              <TableCell align="right">Tamanho</TableCell>
              <TableCell>Solicitado</TableCell>
              <TableCell align="right">Arquivo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lotes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Nenhum lote emitido ainda.
                </TableCell>
              </TableRow>
            ) : null}
            {lotes.map((l) => {
              const r = rotulos[l?.status] ?? { texto: l?.status, cor: "default" };
              const andando = ["na_fila", "processando"].includes(l?.status);
              return (
                <TableRow key={l.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {l?.titulo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(l?.agrupamento ?? []).join(" › ") || "sem agrupamento"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 170 }}>
                    <Chip size="small" label={r.texto} color={r.cor} />
                    {andando ? (
                      <LinearProgress
                        variant={l?.total_previsto ? "determinate" : "indeterminate"}
                        value={l?.progresso_pct ?? 0}
                        sx={{ mt: 0.75, height: 5, borderRadius: 3 }}
                      />
                    ) : null}
                    {l?.status === "erro" && l?.erro ? (
                      <Typography variant="caption" color="error" display="block">
                        {String(l.erro).slice(0, 160)}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    {l?.total_gerado ?? 0}
                    {l?.total_previsto ? ` / ${l.total_previsto}` : ""}
                    {l?.total_falhas ? (
                      <Typography variant="caption" color="warning.main" display="block">
                        {l.total_falhas} com falha
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">{bytesLegivel(l?.arquivo_bytes)}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {dataHora(l?.created_at)}
                      {l?.Solicitante?.name ? (
                        <Box component="span" display="block" color="text.secondary">
                          {l.Solicitante.name}
                        </Box>
                      ) : null}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {l?.arquivo_disponivel ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => baixar(l)}
                      >
                        Baixar
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {l?.status === "expirado" ? "removido" : "—"}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Snackbar
        open={Boolean(aviso)}
        autoHideDuration={6000}
        onClose={() => setAviso("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setAviso("")}>
          {aviso}
        </Alert>
      </Snackbar>
    </Box>
  );
}
